import React, {
  useState,
  useEffect,
  useCallback,
  RefObject,
  SetStateAction
} from 'react'
import styled, { CSSObject } from '@emotion/styled'
import { ProductModifier } from '../Shop/Categories/types'
import includes from 'lodash/includes'
import keyBy from 'lodash/keyBy'
import { formatSelectedModifierV2 } from './utils'
import { SelectedModifiers } from 'shop/components/Landing/types'
import {
  TrackableEvent,
  trackUserActionsFBPixel,
  trackUserActionsGA4
} from 'tracker'
import { CheckboxInput, RadioInput } from '../Inputs'
import { StyledHTMLElement } from 'shop/theme/types'
import QuantityControl from '../QuantityControl/QuantityControl'
import { StyledBinIcon } from '../Shop/commonIcons'
import { formatMoney } from 'shop/utils/common'

interface Props {
  modifier: ProductModifier
  inputType: 'radio' | 'checkbox'
  selectedItems: SelectedModifiers
  setSelectedItems: React.Dispatch<SetStateAction<SelectedModifiers>>
  max: number
  enableIncrement: boolean
  groupId: string
  disabled?: boolean
  min?: number
  defaultChecked?: boolean
}

const Modifier = ({
  setSelectedItems,
  selectedItems,
  modifier,
  inputType,
  max,
  enableIncrement,
  groupId,
  disabled = false,
  min = 0,
  defaultChecked
}: Props) => {
  const { id, name, price, image, inStock } = modifier
  const [quantity, setQuantity] = useState<number>(0)
  // state to handle CSS onClick style changes.
  const [inputPressed, setInputPressed] = useState<string>('')

  const onChange = (
    checked: boolean,
    radioInput?: RefObject<HTMLInputElement>
  ) => {
    const formattedItem = formatSelectedModifierV2(modifier, groupId, quantity)

    const value = (() => {
      if (inputType === 'checkbox' && max !== 1)
        return { ...selectedItems, [id]: formattedItem }
      if (checked) return { [id]: formattedItem }
      return {}
    })()

    setSelectedItems(value)
    setQuantity(checked ? 1 : 0)
    if (radioInput && radioInput.current) radioInput.current.checked = checked
  }

  const track = useCallback(() => {
    if (quantity !== 0) {
      const body = {
        category: name,
        action: TrackableEvent.ProductCustomized,
        label: name,
        value: quantity
      }

      trackUserActionsGA4(body, 'slerpGA4Tracking')

      // legacy tracking
      trackUserActionsFBPixel(TrackableEvent.ProductCustomized, {
        content_name: name,
        content_type: 'modifier',
        contents: [{ id: id, quantity: quantity }]
      })
      trackUserActionsGA4(body, 'merchantGA4Tracking')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity])

  useEffect(() => {
    track()
  }, [track])

  useEffect(() => {
    if (quantity === 0) {
      return setSelectedItems((prevState) => {
        const filteredIds = Object.keys(prevState).filter((key) => key !== id)
        const values = filteredIds.map((modifierId) => prevState[modifierId])
        return { ...keyBy(values, (item) => item.modifier_id) }
      })
    }

    setSelectedItems((prevState) => {
      return {
        ...prevState,
        [id]: {
          ...prevState[id],
          amount: Number(price.base) * quantity,
          discountedAmount: Number(price.discounted) * quantity,
          quantity
        }
      }
    })
  }, [quantity, id, price.base, price.discounted, setSelectedItems])

  const isSelected = includes(Object.keys(selectedItems), id)

  const inputProps = {
    name: `${name}-${groupId}`,
    id: `${id}-${groupId}`,
    checked: isSelected,
    value: id
  }

  const modifierInputDisabled =
    disabled ||
    !inStock ||
    (inputType === 'checkbox' && !enableIncrement && max !== 1)

  const Input = inputType === 'radio' ? RadioInput : CheckboxInput

  useEffect(() => {
    // if modifier within it's group is required and there is only 1, preselect it.
    if (min > 0 && defaultChecked) {
      onChange(true)
      setQuantity(min)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min, defaultChecked])

  return (
    <Container
      {...{ selected: isSelected, disabled: !inStock || disabled }}
      data-testid='modifier-item'
    >
      <Controls>
        <InputWrapper>
          <Input
            data-testid='modifier-item-input'
            {...inputProps}
            type={inputType}
            disabled={modifierInputDisabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              // to handle CSS onClick style changes.
              setInputPressed(inputProps.id)
              onChange(e.target.checked)
              setTimeout(() => setInputPressed(''), 100)
            }}
            inputPressed={inputPressed === inputProps.id}
          />
        </InputWrapper>
      </Controls>
      {image?.thumb && (
        <ImageContainer>
          <Image src={image.thumb} loading='lazy' />
        </ImageContainer>
      )}
      <Info
        htmlFor={`${id}-${groupId}`}
        {...{ selected: isSelected, disabled: !inStock || disabled }}
      >
        <Name>{name}</Name>
      </Info>

      {inputType === 'checkbox' && max !== 1 && isSelected ? (
        <QuantityControl
          quantity={quantity}
          onIncrement={() => {
            setQuantity(quantity + 1)
          }}
          onDecrement={() => {
            setQuantity(quantity - 1)
          }}
          disableIncrement={!enableIncrement}
          width={'32px'}
          fontSize={'14px'}
          customDecrementIcon={quantity === 1 ? <StyledBinIcon /> : undefined}
        />
      ) : (
        <PriceLabel
          basePrice={price.base}
          discountedPrice={price.discounted}
          inStock={inStock}
        />
      )}
    </Container>
  )
}

export const PriceLabel = ({
  basePrice,
  discountedPrice,
  inStock
}: {
  basePrice: string
  discountedPrice: string | null
  inStock: boolean
}) => {
  if (!inStock) return <SoldOutLabel>Unavailable</SoldOutLabel>
  if (Number(basePrice) === 0) return <></>
  return (
    <PriceContent>
      {!!discountedPrice && Number(discountedPrice) !== Number(basePrice) && (
        <DiscountedPrice>{formatMoney(discountedPrice)}</DiscountedPrice>
      )}
      <BasePrice isDiscounted={!!discountedPrice}>
        {formatMoney(basePrice)}
      </BasePrice>
    </PriceContent>
  )
}

const Container = styled.div<{ selected: boolean; disabled: boolean }>(
  ({ theme, selected, disabled }) => ({
    WebkitTapHighlightColor: 'transparent',
    borderBottom: `1px solid ${theme.colors.lineColor}`,
    color: selected ? theme.colors.primary : 'initial',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
    transition: 'box-shadow 100ms ease-in',
    opacity: !disabled ? 1 : 0.5,
    cursor: !disabled ? 'auto' : 'not-allowed',
    minHeight: '64px',
    paddingBottom: '8px',
    '> label': {
      minHeight: '56px',
      height: 'auto',
      justifyContent: 'center',
      flexDirection: 'column'
    }
  })
)

const Name = styled.p(({ theme }) => ({
  fontSize: theme.fontSizes[1],
  margin: '0'
}))

const PriceContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
})

const BasePrice = styled.p<StyledHTMLElement & { isDiscounted: boolean }>(
  ({ theme, isDiscounted }) => ({
    flexShrink: 0,
    color: '#59595A',
    fontSize: theme.fontSizes[1],
    margin: '0',
    textDecoration: isDiscounted ? 'line-through' : 'none'
  })
)

const DiscountedPrice = styled.p(({ theme }) => ({
  flexShrink: 0,
  fontSize: theme.fontSizes[1],
  margin: 0,
  fontWeight: 600,
  textDecoration: 'none',
  color: '#389E0D'
}))

const SoldOutLabel = styled.p(({ theme }) => ({
  flexShrink: 0,
  color: theme.colors.textMute,
  fontSize: theme.fontSizes[1],
  margin: '0'
}))

const ImageContainer = styled.div(() => ({
  height: '54px',
  minWidth: '54px',
  marginRight: '12px'
}))

const Image = styled.img(
  (): CSSObject => ({
    borderRadius: '4px',
    height: '54px',
    width: '54px',
    objectFit: 'cover'
  })
)

const Info = styled.label<
  StyledHTMLElement & { selected: boolean; disabled: boolean }
>(
  ({ selected, disabled }): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    cursor: selected ? 'auto' : !disabled ? 'pointer' : 'not-allowed',
    flexGrow: 1,
    paddingRight: '16px'
  })
)

const Controls = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 18px 4px 0'
}))

const InputWrapper = styled.div(() => ({
  alignSelf: 'flex-start',
  display: 'flex',
  minHeight: '48px',
  alignItems: 'center'
}))

export default Modifier
